import { z } from 'zod'
import { decimalSchema, positiveDecimalSchema, uuidSchema } from './common.ts'
import {
  decimalFitsNumeric,
  materialUnitFactorInputSchema,
  movementUnitSchema,
  warehouseUnitCodeSchema,
} from './units.ts'

const nonZeroDecimal = decimalSchema.refine((value) => !/^-?0(?:\.0+)?$/.test(value), {
  message: 'Кількість не може дорівнювати нулю',
})
const positiveNumericSchema = (precision: number, scale: number) =>
  positiveDecimalSchema.refine(
    (value) => decimalFitsNumeric(value, precision, scale),
    `Значення має відповідати формату numeric(${precision},${scale})`,
  )
export const stockQuantitySchema = decimalSchema.refine(
  (value) => decimalFitsNumeric(value, 18, 3),
  'Кількість має відповідати формату numeric(18,3)',
)
export const positiveStockQuantitySchema = positiveDecimalSchema.refine(
  (value) => decimalFitsNumeric(value, 18, 3),
  'Кількість має відповідати формату numeric(18,3)',
)
export const receiptUnitPriceSchema = positiveDecimalSchema.refine(
  (value) => decimalFitsNumeric(value, 24, 9),
  'Ціна має відповідати формату numeric(24,9)',
)
export const additionalCostUahSchema = decimalSchema
  .refine((value) => !value.startsWith('-'), 'Значення не може бути від’ємним')
  .refine(
    (value) => decimalFitsNumeric(value, 18, 2),
    'Додаткові витрати мають відповідати формату numeric(18,2)',
  )
export const exchangeRateValueSchema = positiveDecimalSchema.refine(
  (value) => decimalFitsNumeric(value, 24, 9),
  'Курс має відповідати формату numeric(24,9)',
)
export const exchangeRateUnitsSchema = positiveDecimalSchema.refine(
  (value) => decimalFitsNumeric(value, 18, 3),
  'Знаменник курсу має відповідати формату numeric(18,3)',
)
export const isoCurrencySchema = z
  .string()
  .trim()
  .regex(/^[A-Za-z]{3}$/)
  .toUpperCase()
const geometrySchema = z.object({
  densityKgM3: positiveNumericSchema(10, 2).nullable().optional(),
  sheetLengthMm: positiveNumericSchema(10, 2).nullable().optional(),
  sheetWidthMm: positiveNumericSchema(10, 2).nullable().optional(),
  thicknessMm: positiveNumericSchema(10, 2).nullable().optional(),
})

export const MATERIAL_CATEGORIES = ['sheet', 'tube', 'angle', 'fastener', 'other'] as const
export const materialCategorySchema = z
  .string()
  .trim()
  .min(1)
  .max(32)
  .regex(/^[a-zA-Z0-9._-]+$/)
export type MaterialCategory = z.infer<typeof materialCategorySchema>
export const MATERIAL_CATEGORY_LABELS: Record<MaterialCategory, string> = {
  sheet: 'Листовий метал',
  tube: 'Труба',
  angle: 'Кутник',
  fastener: 'Кріплення',
  other: 'Інше',
}
export const materialCategoryInputSchema = z.object({
  code: materialCategorySchema,
  name: z.string().trim().min(1).max(120),
})
export const updateMaterialCategoryInputSchema = materialCategoryInputSchema
  .omit({ code: true })
  .partial()
  .refine((input) => Object.keys(input).length > 0)
export const managedMaterialCategorySchema = materialCategoryInputSchema.extend({
  id: uuidSchema,
  archivedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
})
export type MaterialCategoryInput = z.infer<typeof materialCategoryInputSchema>
export type UpdateMaterialCategoryInput = z.infer<typeof updateMaterialCategoryInputSchema>
export type ManagedMaterialCategory = z.infer<typeof managedMaterialCategorySchema>

const materialInputBaseSchema = z
  .object({
    name: z.string().trim().min(1).max(240),
    grade: z.string().trim().max(120).nullable().optional(),
    category: materialCategorySchema,
    baseUnit: warehouseUnitCodeSchema,
    qrCode: z.string().trim().min(1).max(240),
    defaultLocationId: uuidSchema.nullable().optional(),
    criticalThreshold: positiveNumericSchema(18, 3).nullable().optional(),
    unitFactors: z.array(materialUnitFactorInputSchema).max(100).default([]),
  })
  .merge(geometrySchema)
const refineUnitFactors = (
  input: { baseUnit: string; unitFactors: { unitCode: string }[] },
  context: z.RefinementCtx,
) => {
  if (input.unitFactors.some((factor) => factor.unitCode === input.baseUnit))
    context.addIssue({
      code: 'custom',
      path: ['unitFactors'],
      message: 'Базова одиниця не потребує коефіцієнта',
    })
  if (new Set(input.unitFactors.map((factor) => factor.unitCode)).size !== input.unitFactors.length)
    context.addIssue({ code: 'custom', path: ['unitFactors'], message: 'Одиниця повторюється' })
}
export const materialInputSchema = materialInputBaseSchema.superRefine(refineUnitFactors)
/** A new material may leave its QR code out; the server then generates one for the label. */
export const createMaterialInputSchema = materialInputBaseSchema
  .extend({ qrCode: materialInputBaseSchema.shape.qrCode.optional() })
  .superRefine(refineUnitFactors)
export const updateMaterialInputSchema = materialInputBaseSchema
  .omit({ baseUnit: true, unitFactors: true })
  .partial()
  .extend({ unitFactors: z.array(materialUnitFactorInputSchema).max(100).optional() })
  .refine((input) => Object.keys(input).length > 0)
export type CreateMaterialInput = z.infer<typeof createMaterialInputSchema>
export type UpdateMaterialInput = z.infer<typeof updateMaterialInputSchema>

export const stockMovementKindSchema = z.enum([
  'receipt',
  'issue',
  'reserve',
  'release',
  'writeoff',
  'adjustment',
  'transfer_out',
  'transfer_in',
])
export type StockMovementKind = z.infer<typeof stockMovementKindSchema>
export const createStockMovementKindSchema = z.enum(['receipt', 'issue', 'writeoff', 'adjustment'])
export const manualExchangeRateInputSchema = z.object({
  rate: exchangeRateValueSchema,
  sourceUrl: z.string().url(),
  reason: z.string().trim().min(1).max(1000),
})
export const createStockMovementInputSchema = z
  .object({
    materialId: uuidSchema,
    kind: createStockMovementKindSchema,
    quantity: stockQuantitySchema.and(nonZeroDecimal),
    unit: movementUnitSchema,
    locationId: uuidSchema,
    orderId: uuidSchema.optional(),
    note: z.string().trim().max(1000).optional(),
    supplierId: uuidSchema.optional(),
    buyingSellerEntityId: uuidSchema.optional(),
    source: z.string().trim().min(1).max(240).optional(),
    receiptDate: z.iso.date().optional(),
    unitPrice: receiptUnitPriceSchema.optional(),
    currency: isoCurrencySchema.optional(),
    additionalCostUah: additionalCostUahSchema.optional(),
    manualRate: manualExchangeRateInputSchema.optional(),
  })
  .superRefine((input, context) => {
    if (input.kind !== 'adjustment' && input.quantity.startsWith('-'))
      context.addIssue({
        code: 'custom',
        path: ['quantity'],
        message: 'Кількість має бути додатною',
      })
    if (input.kind === 'adjustment' && !input.note?.trim())
      context.addIssue({
        code: 'custom',
        path: ['note'],
        message: 'Вкажіть причину коригування',
      })
    if (input.kind === 'receipt' && !input.supplierId)
      context.addIssue({
        code: 'custom',
        path: ['supplierId'],
        message: 'Постачальник обов’язковий',
      })
    if (input.kind === 'receipt' && !input.buyingSellerEntityId)
      context.addIssue({
        code: 'custom',
        path: ['buyingSellerEntityId'],
        message: 'Покупця від VEEM обов’язково вказати',
      })
    if (input.kind === 'receipt' && !input.source)
      context.addIssue({
        code: 'custom',
        path: ['source'],
        message: 'Джерело надходження обов’язкове',
      })
    if (input.kind === 'receipt' && !input.receiptDate)
      context.addIssue({
        code: 'custom',
        path: ['receiptDate'],
        message: 'Дата надходження обов’язкова',
      })
    if (input.kind === 'receipt' && !input.unitPrice)
      context.addIssue({
        code: 'custom',
        path: ['unitPrice'],
        message: 'Ціна за одиницю обов’язкова',
      })
    if (input.kind === 'receipt' && !input.currency)
      context.addIssue({
        code: 'custom',
        path: ['currency'],
        message: 'Валюта обов’язкова',
      })
    if (input.kind === 'receipt' && input.currency === 'UAH' && input.manualRate)
      context.addIssue({
        code: 'custom',
        path: ['manualRate'],
        message: 'Для UAH курс завжди дорівнює 1 за офіційним правилом НБУ',
      })
  })
export type CreateStockMovementInput = z.infer<typeof createStockMovementInputSchema>

export const warehouseInputSchema = z.object({ name: z.string().trim().min(1).max(120) })
export const warehouseSchema = warehouseInputSchema.extend({
  id: uuidSchema,
  archivedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
})
export type Warehouse = z.infer<typeof warehouseSchema>
export const locationInputSchema = z.object({
  warehouseId: uuidSchema,
  name: z.string().trim().min(1).max(160),
})
export const locationSchema = locationInputSchema.extend({
  id: uuidSchema,
  archivedAt: z.string().nullable(),
  warehouseName: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
})
export type WarehouseLocation = z.infer<typeof locationSchema>
/** The prototype's fixed set; NULL on a supplier means the terms are not known yet. */
export const SUPPLIER_PAYMENT_TERMS = [
  'prepay',
  'deferred_7',
  'deferred_14',
  'deferred_30',
] as const
export type SupplierPaymentTerms = (typeof SUPPLIER_PAYMENT_TERMS)[number]
export const SUPPLIER_PAYMENT_TERMS_LABELS: Record<SupplierPaymentTerms, string> = {
  prepay: 'Передоплата 100%',
  deferred_7: 'Відстрочка 7 днів',
  deferred_14: 'Відстрочка 14 днів',
  deferred_30: 'Відстрочка 30 днів',
}

export const supplierInputSchema = z.object({
  displayName: z.string().trim().min(1).max(240),
  legalName: z.string().trim().max(240).nullable().optional(),
  edrpou: z
    .string()
    .trim()
    .regex(/^\d{8,10}$/)
    .nullable()
    .optional(),
  contactName: z.string().trim().max(160).nullable().optional(),
  phone: z.string().trim().max(64).nullable().optional(),
  email: z.string().trim().email().max(240).nullable().optional(),
  address: z.string().trim().max(500).nullable().optional(),
  city: z.string().trim().max(120).nullable().optional(),
  paymentTerms: z.enum(SUPPLIER_PAYMENT_TERMS).nullable().optional(),
  note: z.string().trim().max(2000).nullable().optional(),
})
export const supplierSchema = supplierInputSchema.extend({
  id: uuidSchema,
  archivedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
})
export type Supplier = z.infer<typeof supplierSchema>
export type SupplierInput = z.infer<typeof supplierInputSchema>
export const ORDERED_SUPPLY_STATUSES = [
  'draft',
  'ordered',
  'in_transit',
  'partially_received',
  'received',
  'cancelled',
] as const
export const orderedSupplyStatusSchema = z.enum(ORDERED_SUPPLY_STATUSES)
export type OrderedSupplyStatus = z.infer<typeof orderedSupplyStatusSchema>
export const ORDERED_SUPPLY_STATUS_LABELS: Record<OrderedSupplyStatus, string> = {
  draft: 'Чернетка',
  ordered: 'Замовлено',
  in_transit: 'У дорозі',
  partially_received: 'Частково отримано',
  received: 'Отримано',
  cancelled: 'Скасовано',
}
export const orderedSupplyInputSchema = z.object({
  materialId: uuidSchema,
  supplierId: uuidSchema,
  locationId: uuidSchema,
  quantity: positiveStockQuantitySchema,
  unit: warehouseUnitCodeSchema,
  receivedQuantity: stockQuantitySchema
    .refine((value) => !value.startsWith('-'), 'Отримана кількість не може бути від’ємною')
    .default('0'),
  status: orderedSupplyStatusSchema.default('draft'),
  expectedAt: z.iso.date().nullable().optional(),
  note: z.string().trim().max(1000).nullable().optional(),
})
export const updateOrderedSupplyInputSchema = orderedSupplyInputSchema
  .partial()
  .refine((input) => Object.keys(input).length > 0)
export const transferStockInputSchema = z
  .object({
    materialId: uuidSchema,
    sourceLocationId: uuidSchema,
    destinationLocationId: uuidSchema,
    quantity: positiveStockQuantitySchema,
    unit: warehouseUnitCodeSchema,
    note: z.string().trim().max(1000).optional(),
  })
  .refine((input) => input.sourceLocationId !== input.destinationLocationId, {
    message: 'Оберіть інше місце призначення',
    path: ['destinationLocationId'],
  })
export const exchangeRateSnapshotSchema = z
  .object({
    currency: z.string().regex(/^[A-Z]{3}$/),
    requestedRateDate: z.iso.date(),
    effectiveRateDate: z.iso.date(),
    rate: exchangeRateValueSchema,
    units: exchangeRateUnitsSchema,
    provider: z.enum(['NBU', 'manual']),
    sourceUrl: z.string().url(),
    fetchedAt: z.string(),
    manualReason: z.string().nullable(),
  })
  .superRefine((rate, context) => {
    if (
      rate.currency === 'UAH' &&
      (rate.rate !== '1' ||
        rate.units !== '1' ||
        rate.provider !== 'NBU' ||
        rate.sourceUrl !== 'https://bank.gov.ua/NBUStatService/v1/statdirectory/exchange' ||
        rate.manualReason)
    )
      context.addIssue({
        code: 'custom',
        message: 'Для UAH дозволено лише офіційний курс 1:1',
      })
    if (rate.provider === 'manual' && !rate.manualReason?.trim())
      context.addIssue({ code: 'custom', path: ['manualReason'], message: 'Вкажіть причину курсу' })
    if (rate.provider === 'NBU' && rate.manualReason)
      context.addIssue({ code: 'custom', path: ['manualReason'], message: 'Курс НБУ не є ручним' })
  })

export type ExchangeRateSnapshot = z.infer<typeof exchangeRateSnapshotSchema>

export const stockMovementSchema = z.object({
  id: uuidSchema,
  kind: stockMovementKindSchema,
  materialName: z.string(),
  materialGrade: z.string().nullable(),
  materialBaseUnit: warehouseUnitCodeSchema,
  qty: decimalSchema,
  enteredUnit: warehouseUnitCodeSchema,
  enteredQuantity: decimalSchema,
  conversionFactor: positiveDecimalSchema,
  warehouseName: z.string(),
  locationName: z.string(),
  valueUah: decimalSchema,
  unitCostUah: decimalSchema,
  originalUnitPrice: decimalSchema.nullable(),
  originalCurrency: z
    .string()
    .regex(/^[A-Z]{3}$/)
    .nullable(),
  receiptDate: z.iso.date().nullable(),
  additionalCostUah: decimalSchema.nullable(),
  exchangeRateSnapshotId: uuidSchema.nullable(),
  supplierName: z.string().nullable(),
  supplierLegalName: z.string().nullable(),
  supplierEdrpou: z.string().nullable(),
  supplierContactName: z.string().nullable(),
  supplierPhone: z.string().nullable(),
  supplierEmail: z.string().nullable(),
  supplierAddress: z.string().nullable(),
  buyingSellerEntityName: z.string().nullable(),
  buyingSellerEntityEdrpou: z.string().nullable(),
  source: z.string().nullable(),
  ttnNumber: z.string().nullable(),
  note: z.string().nullable(),
  createdAt: z.string(),
  createdBy: z.string(),
  orderId: uuidSchema.nullable(),
  correlationId: uuidSchema.nullable(),
})
export type StockMovement = z.infer<typeof stockMovementSchema>

export const warehouseMaterialSchema = materialInputBaseSchema.extend({
  rowId: z.string(),
  id: uuidSchema,
  archived: z.boolean(),
  categoryName: z.string(),
  hasPhoto: z.boolean(),
  baseUnitSymbol: z.string(),
  onHand: decimalSchema,
  reserved: decimalSchema,
  available: decimalSchema,
  critical: z.boolean(),
  location: z.string().nullable().optional(),
  locationId: uuidSchema.nullable(),
  warehouseName: z.string().nullable().optional(),
  warehouseId: uuidSchema.nullable(),
  ordered: decimalSchema.optional(),
  physicalValueUah: decimalSchema.optional(),
  reservedValueUah: decimalSchema.optional(),
})
export type WarehouseMaterial = z.infer<typeof warehouseMaterialSchema>

export const materialPhotoSchema = z.object({
  id: uuidSchema,
  fileName: z.string().min(1),
  contentType: z.string().min(1),
  sizeBytes: z.number().int().positive(),
  url: z.string().url(),
  expiresInSeconds: z.number().int().positive(),
})
export type MaterialPhoto = z.infer<typeof materialPhotoSchema>

/**
 * The supplier page: the card, earlier names, the summary tiles and the tabs. Money (unit costs,
 * receipt values, payments, the unpaid total) is null or empty for roles that may not see it.
 */
export const supplierDetailSchema = z.object({
  supplier: supplierSchema,
  previousNames: z.array(z.object({ name: z.string(), replacedAt: z.string() })),
  summary: z.object({
    materials: z.int().nonnegative(),
    openOrders: z.int().nonnegative(),
    /** Payment requests still pending or approved, not yet paid. */
    unpaid: decimalSchema.nullable(),
    lastReceiptAt: z.string().nullable(),
  }),
  materials: z.array(
    z.object({
      materialId: uuidSchema,
      materialName: z.string(),
      materialGrade: z.string().nullable(),
      baseUnitSymbol: z.string(),
      /** Free stock now, all locations, in the base unit. */
      available: decimalSchema,
      /** UAH per base unit at the latest receipt from this supplier, unrounded (scale 9). */
      lastUnitCost: decimalSchema.nullable(),
      lastReceiptAt: z.string().nullable(),
    }),
  ),
  receipts: z.array(
    z.object({
      id: uuidSchema,
      materialId: uuidSchema,
      materialName: z.string(),
      quantity: decimalSchema,
      baseUnitSymbol: z.string(),
      valueUah: decimalSchema.nullable(),
      supplierName: z.string().nullable(),
      /** The delivery document the storekeeper entered («Накладна № 1184»). */
      document: z.string().nullable(),
      createdAt: z.string(),
    }),
  ),
  orders: z.array(
    z.object({
      orderNumber: z.string(),
      materials: z.string(),
      expectedAt: z.string().nullable(),
      amount: decimalSchema.nullable(),
      delivery: z.enum(['ordered', 'in_transit', 'partially_received', 'received', 'cancelled']),
    }),
  ),
  payments: z.array(
    z.object({
      id: uuidSchema,
      amount: decimalSchema,
      status: z.enum(['pending', 'approved', 'rejected', 'paid']),
      purpose: z.string(),
      supplierInvoiceNumber: z.string().nullable(),
      /** Supplier order number(s) the payment is for, comma-separated; null when it pays none. */
      supplyOrder: z.string().nullable(),
      dueAt: z.string().nullable(),
      createdAt: z.string(),
    }),
  ),
})
export type SupplierDetail = z.infer<typeof supplierDetailSchema>

/** A row of «Постачальники»: the supplier and what is open with it. */
export const supplierOverviewSchema = supplierSchema
  .omit({ createdAt: true, updatedAt: true })
  .extend({
    materials: z.array(z.string()),
    openOrders: z.int().nonnegative(),
    /** Pending and approved payment requests; null for roles that may not see money. */
    unpaid: decimalSchema.nullable(),
    lastReceiptAt: z.string().nullable(),
  })
export type SupplierOverview = z.infer<typeof supplierOverviewSchema>

/** How a material stands, worst first; the badge always has words, never colour alone. */
export const STOCK_STATES = ['out', 'shortage', 'critical', 'reserved', 'ok'] as const
export type StockState = (typeof STOCK_STATES)[number]

/** A row of «Залишки»: one material across every place it lies, in its base unit. */
export const warehouseStockRowSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  grade: z.string().nullable(),
  categoryName: z.string(),
  baseUnitSymbol: z.string(),
  criticalThreshold: decimalSchema.nullable(),
  locations: z.array(
    z.object({ warehouseName: z.string(), locationName: z.string(), physical: decimalSchema }),
  ),
  physical: decimalSchema,
  reserved: decimalSchema,
  /** Physical minus reserved, never below zero. */
  available: decimalSchema,
  /** Still to arrive on open supplier orders. */
  ordered: decimalSchema,
  state: z.enum(STOCK_STATES),
  /** Stock value in UAH at average cost; null for roles that may not see it. */
  valueUah: decimalSchema.nullable(),
  /** Everyone who delivered or was ordered this material, for search. */
  supplierNames: z.array(z.string()),
})
export type WarehouseStockRow = z.infer<typeof warehouseStockRowSchema>

export const warehouseStockSchema = z.object({
  materials: z.array(warehouseStockRowSchema),
  /** Open supplier order lines and the nearest date one is expected. */
  expected: z.object({ count: z.int().nonnegative(), nearestAt: z.string().nullable() }),
})
export type WarehouseStock = z.infer<typeof warehouseStockSchema>

/** A material's page: its card, balance and state, where it is promised, coming and used. */
export const warehouseMaterialPageSchema = z.object({
  stock: warehouseStockRowSchema,
  qrCode: z.string(),
  baseUnit: z.string(),
  defaultPlace: z.object({ warehouseName: z.string(), locationName: z.string() }).nullable(),
  units: z.array(
    z.object({ unitCode: z.string(), unitSymbol: z.string(), factorToBase: decimalSchema }),
  ),
  /** UAH per base unit at average cost; null for roles that may not see stock value. */
  averageCostUah: decimalSchema.nullable(),
  lastSupplier: z.object({ id: uuidSchema, name: z.string() }).nullable(),
  /** Every place the material has been, empty ones too. */
  places: z.array(
    z.object({ warehouseName: z.string(), locationName: z.string(), physical: decimalSchema }),
  ),
  /** Orders still short of it beyond their reserve; empty unless the material is short. */
  shortages: z.array(
    z.object({ orderId: uuidSchema, orderNumber: z.string(), quantity: decimalSchema }),
  ),
  reservations: z.array(
    z.object({
      orderId: uuidSchema,
      orderNumber: z.string(),
      productName: z.string().nullable(),
      quantity: decimalSchema,
    }),
  ),
  expected: z.array(
    z.object({
      supplierName: z.string(),
      status: z.enum(['ordered', 'in_transit', 'partially_received']),
      expectedAt: z.string().nullable(),
      quantity: decimalSchema,
    }),
  ),
  /** Products whose latest released technology card uses it, per one product. */
  usedIn: z.array(z.object({ productName: z.string(), quantity: decimalSchema })),
  moves: z.array(
    z.object({
      id: uuidSchema,
      kind: z.string(),
      at: z.string(),
      quantity: decimalSchema,
      enteredQuantity: decimalSchema,
      enteredUnitSymbol: z.string(),
      warehouseName: z.string().nullable(),
      locationName: z.string().nullable(),
      orderId: uuidSchema.nullable(),
      orderNumber: z.string().nullable(),
      note: z.string().nullable(),
      source: z.string().nullable(),
      actorName: z.string().nullable(),
    }),
  ),
})
export type WarehouseMaterialPage = z.infer<typeof warehouseMaterialPageSchema>

/**
 * «Видача і приймання»: the storekeeper's two daily flows. Kits for production orders with an
 * active supply plan, what is ordered and still coming, and today's issues and receipts.
 */
export const warehouseFlowSchema = z.object({
  kits: z.array(
    z.object({
      orderId: uuidSchema,
      orderNumber: z.string(),
      productName: z.string().nullable(),
      dealName: z.string().nullable(),
      clientName: z.string().nullable(),
      deadline: z.string().nullable(),
      /** `ready` once free stock covers what is not reserved yet; the kit can then be issued. */
      state: z.enum(['ready', 'shortage']),
      materials: z.array(
        z.object({
          materialId: uuidSchema,
          materialName: z.string(),
          baseUnitSymbol: z.string(),
          required: decimalSchema,
          reserved: decimalSchema,
          /** What free stock cannot cover, after orders with lower numbers take theirs. */
          shortage: decimalSchema,
          /** An open purchase request for this order and material already exists. */
          requested: z.boolean(),
          /** The nearest delivery still coming for the material. */
          incoming: z
            .object({
              supplierName: z.string(),
              quantity: decimalSchema,
              expectedAt: z.string().nullable(),
            })
            .nullable(),
          /** Where the material physically lies. */
          places: z.array(
            z.object({
              warehouseName: z.string(),
              locationName: z.string(),
              physical: decimalSchema,
            }),
          ),
        }),
      ),
    }),
  ),
  expected: z.array(
    z.object({
      supplyId: uuidSchema,
      supplyOrderNumber: z.string(),
      materialId: uuidSchema,
      materialName: z.string(),
      baseUnitSymbol: z.string(),
      supplierName: z.string(),
      supplierInvoiceNumber: z.string().nullable(),
      status: z.enum(['ordered', 'in_transit', 'partially_received']),
      expectedAt: z.string().nullable(),
      quantity: decimalSchema,
      received: decimalSchema,
      /** UAH per base unit; null for roles that may not see stock value. */
      unitCost: decimalSchema.nullable(),
      /** The supply's own place, where receiving puts it by default. */
      locationId: uuidSchema,
      /** The company that pays; null when the supply was ordered without one. */
      payerEntityId: uuidSchema.nullable(),
      /** Production orders short of this material. */
      waitingOrders: z.array(z.object({ orderId: uuidSchema, orderNumber: z.string() })),
    }),
  ),
  /** Issues and receipts posted today, Kyiv time, newest first. */
  today: z.array(
    z.object({
      id: uuidSchema,
      kind: z.enum(['issue', 'receipt']),
      at: z.string(),
      materialId: uuidSchema,
      materialName: z.string(),
      baseUnitSymbol: z.string(),
      quantity: decimalSchema,
      orderNumber: z.string().nullable(),
      ttnNumber: z.string().nullable(),
      source: z.string().nullable(),
      actorName: z.string().nullable(),
    }),
  ),
})
export type WarehouseFlow = z.infer<typeof warehouseFlowSchema>

/** Receiving all or part of an ordered delivery, in the material's base unit. */
export const receiveOrderedSupplyInputSchema = z.strictObject({
  quantity: stockQuantitySchema
    .and(nonZeroDecimal)
    .refine((value) => !value.startsWith('-'), 'Кількість має бути додатною'),
  locationId: uuidSchema,
  /** The waybill or invoice the goods came with. */
  document: z.string().trim().min(1).max(240),
  /** UAH per base unit; needed only when the supply has no agreed price. It becomes the
   * supply's price, which is kept to the kopeck. */
  unitPrice: receiptUnitPriceSchema
    .refine((value) => !/\.\d{3,}$/.test(value), 'Ціна — до копійок')
    .optional(),
  /** The company that pays; needed only when the supply names none. */
  buyingSellerEntityId: uuidSchema.optional(),
})
export type ReceiveOrderedSupplyInput = z.infer<typeof receiveOrderedSupplyInputSchema>
